import { useEffect, useState } from "react";
import { COLORS } from "../lib/brand";
import { ActivityIndicator, Text, View } from "react-native";

import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Modal } from "./ui/Modal";
import {
  addToShortlist,
  isShortlisted,
  removeFromShortlist,
  updateShortlistNotes,
} from "../lib/shortlist";
import { confirmAndRun } from "../lib/confirm";

export function ShortlistButton({
  competitionId,
  studentId,
}: {
  competitionId: string;
  studentId: string;
}) {
  const [loading, setLoading] = useState(true);
  const [entryId, setEntryId] = useState<string | null>(null);
  const [notes, setNotes] = useState<string | null>(null);
  const [draftNotes, setDraftNotes] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const hit = await isShortlisted(competitionId, studentId);
      setEntryId(hit?.id ?? null);
      setNotes(hit?.notes ?? null);
      setDraftNotes(hit?.notes ?? "");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId, studentId]);

  async function onSave() {
    setErr(null);
    setBusy(true);
    try {
      if (entryId) {
        await updateShortlistNotes(entryId, draftNotes || null);
      } else {
        await addToShortlist({ competitionId, studentId, notes: draftNotes || undefined });
      }
      await refresh();
      setOpen(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not save shortlist");
    } finally {
      setBusy(false);
    }
  }

  async function onRemove() {
    if (!entryId) return;
    void confirmAndRun("Remove this candidate from your shortlist?", async () => {
      setErr(null);
      setBusy(true);
      try {
        await removeFromShortlist(entryId);
        await refresh();
        setOpen(false);
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Could not remove");
      } finally {
        setBusy(false);
      }
    });
  }

  if (loading) {
    return (
      <View className="rounded-btn border border-sand-dark bg-white px-3 py-2">
        <ActivityIndicator size="small" color={COLORS.FOREST.hex} />
      </View>
    );
  }

  const shortlisted = !!entryId;
  return (
    <>
      <Button
        variant={shortlisted ? "secondary" : "ghost-light"}
        size="sm"
        theme="light"
        onPress={() => {
          setDraftNotes(notes ?? "");
          setOpen(true);
        }}
      >
        {shortlisted ? "★ Shortlisted" : "+ Shortlist"}
      </Button>

      <Modal
        visible={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        title={shortlisted ? "Edit shortlist notes" : "Add to shortlist"}
        theme="light"
      >
        <Text className="mb-4 text-[12px] leading-5 text-text/60" style={{ fontFamily: "DMSans_400Regular" }}>
          Keep private notes for this candidate in this competition.
        </Text>
        <Input label="Notes" value={draftNotes} onChangeText={setDraftNotes} placeholder="Optional notes" multiline variant="light" />
        {err ? (
          <Text className="mt-2 text-xs text-error" style={{ fontFamily: "DMSans_400Regular" }}>
            {err}
          </Text>
        ) : null}
        <View className="mt-5 flex-row flex-wrap justify-end gap-2">
          {shortlisted ? (
            <Button variant="ghost-light" size="sm" theme="light" disabled={busy} onPress={() => void onRemove()}>
              Remove
            </Button>
          ) : null}
          <Button variant="ghost-light" size="sm" theme="light" disabled={busy} onPress={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" theme="light" disabled={busy} onPress={() => void onSave()}>
            {shortlisted ? "Save notes" : "Add shortlist"}
          </Button>
        </View>
      </Modal>
    </>
  );
}
